import { compass, v3, dot } from "./vec3";
import { validPolygon, triangulate } from "./footprint";

const number = (v) => v === "" || v == null ? null : Number(v);
export const roofZ = (roof, x, y) => {
  const d = compass(Number(roof.azimuthDeg));
  return -Math.tan(Number(roof.pitchDeg) * Math.PI / 180) * (x * d.x + y * d.y);
};

export function siteGeometryErrors(value) {
  if (!value) return [];
  const errors = [];
  const positive = (v) => Number.isFinite(number(v)) && number(v) > 0;
  const nonnegative = (v) => Number.isFinite(number(v)) && number(v) >= 0;
  if (value.frame && ![value.frame.eastM, value.frame.northM, value.frame.elevationM].every((v) => Number.isFinite(number(v)))) errors.push("frame");
  if (value.roof) {
    const r = value.roof;
    if (!positive(r.widthM) || !positive(r.depthM) || !positive(r.heightM) || (r.footprint && !validPolygon(r.footprint))) errors.push("roofDimensions");
    if (!nonnegative(r.pitchDeg) || number(r.pitchDeg) >= 75 ||
        !nonnegative(r.azimuthDeg) || number(r.azimuthDeg) >= 360) errors.push("roofAngles");
    if (!nonnegative(r.setbackM) || number(r.setbackM) * 2 >= Math.min(number(r.widthM), number(r.depthM))) errors.push("roofSetback");
    if (roofFootprint(r).some((p) => number(r.heightM) + roofZ(r, p.x, p.y) <= 0)) errors.push("roofBelowStreet");
  }
  if ((value.obstacles || []).some((o) =>
    ((o.kind === "polygon" || (o.kind === "roof" && o.footprint)) && !validPolygon(o.footprint)) ||
    (o.kind === "roof" && (!nonnegative(o.pitchDeg) || number(o.pitchDeg) >= 75 ||
      obstacleFootprint(o).some((p) => number(o.heightM) + roofZ({ pitchDeg: o.pitchDeg, azimuthDeg: o.rotationDeg },
        p.x - number(o.eastM), p.y - number(o.northM)) <= 0))) ||
    (o.kind === "tree" && (!nonnegative(o.trunkHeightM) || number(o.trunkHeightM) >= number(o.heightM) ||
      (o.crownShape != null && !["round", "cone"].includes(o.crownShape)))) ||
    !positive(o.widthM) || !positive(o.depthM) || !positive(o.heightM) ||
    ![o.eastM, o.northM, o.rotationDeg].every((v) => Number.isFinite(number(v))) ||
    !nonnegative(o.baseHeightM) || !["roof", "street"].includes(o.base) ||
    (o.base === "roof" && !value.roof))) errors.push("obstacleDimensions");
  return errors;
}

export function rectangleCorners(width, depth, azimuth, east = 0, north = 0, inset = 0) {
  const along = compass(Number(azimuth) - 90);
  const across = compass(Number(azimuth));
  return [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([a, b]) => ({
    x: Number(east) + along.x * a * (Number(width) / 2 - inset) + across.x * b * (Number(depth) / 2 - inset),
    y: Number(north) + along.y * a * (Number(width) / 2 - inset) + across.y * b * (Number(depth) / 2 - inset),
  }));
}

export const roofFootprint = (roof) => roof.footprint || rectangleCorners(roof.widthM, roof.depthM, roof.azimuthDeg);

// Display real extrema of the drawn outline without changing the stored datum.
export function roofHeightRange(roof) {
  if (!roof || number(roof.heightM) == null) return null;
  const offsets = roofFootprint(roof).map((p) => roofZ(roof, p.x, p.y));
  if (!offsets.length || !offsets.every(Number.isFinite)) return null;
  const minimumOffset = Math.min(...offsets), maximumOffset = Math.max(...offsets);
  return { minimum: Number(roof.heightM) + minimumOffset, maximum: Number(roof.heightM) + maximumOffset,
    minimumOffset, maximumOffset };
}

const segmentDistance = (p, a, b) => {
  const dx = b.x - a.x, dy = b.y - a.y;
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy || 1)));
  return Math.hypot(p.x - a.x - t * dx, p.y - a.y - t * dy);
};
function insideRing(p, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i], b = ring[j];
    if (segmentDistance(p, a, b) < 1e-7) return true;
    if ((a.y > p.y) !== (b.y > p.y) && p.x < (b.x - a.x) * (p.y - a.y) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}
export function fitsFootprint(corners, ring, setback = 0) {
  if (corners.some((p) => !insideRing(p, ring))) return false;
  const cross2 = (a, b, p) => (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
  for (let i = 0; i < corners.length; i++) {
    const a = corners[i], b = corners[(i + 1) % corners.length];
    if (!insideRing({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, ring)) return false;
    for (let j = 0; j < ring.length; j++) {
      const c = ring[j], d = ring[(j + 1) % ring.length];
      if (cross2(a, b, c) * cross2(a, b, d) < -1e-12 && cross2(c, d, a) * cross2(c, d, b) < -1e-12) return false;
      if (Math.min(segmentDistance(a, c, d), segmentDistance(b, c, d), segmentDistance(c, a, b), segmentDistance(d, a, b)) < setback - 1e-6) return false;
    }
  }
  return true;
}

function prism(points, bottom, top, adjacentRoof = null) {
  // Every generated solid needs outward-facing walls, regardless of the
  // direction in which its footprint was drawn.
  const area = points.reduce((sum, p, i) => sum + p.x * points[(i + 1) % points.length].y - points[(i + 1) % points.length].x * p.y, 0);
  if (area < 0) points = [...points].reverse();
  const low = points.map((p) => [p.x, p.y, bottom(p)]);
  const high = points.map((p) => [p.x, p.y, top(p)]);
  const triangles = triangulate(points).flatMap(([a, b, c]) => [[high[a], high[b], high[c]], [low[a], low[c], low[b]]]);
  for (let i = 0; i < points.length; i += 1) {
    const j = (i + 1) % points.length;
    const neighbor = adjacentRoof?.(points[i], points[j]);
    if (!neighbor) {
      triangles.push([low[i], low[j], high[j]], [low[i], high[j], high[i]]);
      continue;
    }
    // A roof joint exposes only the difference between the two surfaces.
    // Using the street as the bottom here creates a full-height internal wall
    // whenever adjacent planes have a step, making a hip roof look like a box.
    const a = points[i], b = points[j];
    const otherA = neighbor.top(a), otherB = neighbor.top(b);
    const diffA = high[i][2] - otherA, diffB = high[j][2] - otherB;
    if (Math.max(diffA, diffB) <= 0.03) continue;
    const wall = (start, end) => {
      const at = (t) => {
        const p = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
        return { lower: [p.x, p.y, neighbor.top(p)], upper: [p.x, p.y, top(p)] };
      };
      const left = at(start), right = at(end);
      if (right.upper[2] - right.lower[2] > 1e-7)
        triangles.push([left.lower, right.lower, right.upper]);
      if (left.upper[2] - left.lower[2] > 1e-7)
        triangles.push([left.lower, right.upper, left.upper]);
    };
    if (diffA >= 0 && diffB >= 0) wall(0, 1);
    else {
      const crossing = diffA / (diffA - diffB);
      if (diffA > 0) wall(0, crossing);
      else wall(crossing, 1);
    }
  }
  return new Float32Array(triangles.flat(2));
}

export const obstacleFootprint = (o) => o.kind === "polygon" || (o.kind === "roof" && o.footprint) ? (o.footprint || []).map((p) => ({ x: p.x + Number(o.eastM), y: p.y + Number(o.northM) }))
  : o.kind === "cylinder" ? Array.from({ length: 32 }, (_, i) => ({
    x: Number(o.eastM) + Number(o.widthM) / 2 * Math.cos(i * Math.PI / 16),
    y: Number(o.northM) + Number(o.widthM) / 2 * Math.sin(i * Math.PI / 16),
  }))
  : rectangleCorners(o.widthM, o.depthM, o.rotationDeg, o.eastM, o.northM);

export function obstacleBaseZ(value, o, p, groundZ = () => 0) {
  return (o.base === "roof" ? roofZ(value.roof, p.x, p.y)
    : value.roof ? -Number(value.roof.heightM) : groundZ(p.x, p.y)) + Number(o.baseHeightM);
}

export function obstacleTopZ(value, o, p, groundZ = () => 0) {
  return obstacleBaseZ(value, o, p, groundZ) + Number(o.heightM) + (o.kind === "roof"
    ? roofZ({ pitchDeg: o.pitchDeg, azimuthDeg: o.rotationDeg }, p.x - Number(o.eastM), p.y - Number(o.northM)) : 0);
}

function treeMesh(o, base) {
  // An opaque ellipsoidal crown: conservative geometry, not a seasonal foliage model.
  const z0 = base({ x: Number(o.eastM), y: Number(o.northM) });
  const h = Number(o.heightM), trunk = Number(o.trunkHeightM), rz = (h - trunk) / 2;
  if (o.crownShape === "cone") {
    const ring = Array.from({ length: 24 }, (_, i) => {
      const angle = 2 * Math.PI * i / 24;
      return [Number(o.eastM) + Number(o.widthM) / 2 * Math.cos(angle),
        Number(o.northM) + Number(o.depthM) / 2 * Math.sin(angle), z0 + trunk];
    });
    const apex = [Number(o.eastM), Number(o.northM), z0 + h];
    const centre = [Number(o.eastM), Number(o.northM), z0 + trunk];
    const faces = ring.flatMap((p, i) => [p, ring[(i + 1) % ring.length], apex, ring[(i + 1) % ring.length], p, centre]);
    return combineTriangles(new Float32Array(faces.flat()), prism(rectangleCorners(Math.min(0.3, Number(o.widthM) / 8), Math.min(0.3, Number(o.depthM) / 8), 0, o.eastM, o.northM), () => z0, () => z0 + trunk));
  }
  const point = (i, j) => {
    const phi = Math.PI * i / 8, theta = 2 * Math.PI * j / 16;
    return [Number(o.eastM) + Number(o.widthM) / 2 * Math.sin(phi) * Math.cos(theta),
      Number(o.northM) + Number(o.depthM) / 2 * Math.sin(phi) * Math.sin(theta), z0 + trunk + rz * (1 + Math.cos(phi))];
  };
  const faces = [];
  for (let i = 0; i < 8; i += 1) for (let j = 0; j < 16; j += 1) {
    const a = point(i, j), b = point(i, j + 1), c = point(i + 1, j + 1), d = point(i + 1, j);
    faces.push(a, b, c, a, c, d);
  }
  return combineTriangles(new Float32Array(faces.flat()), prism(rectangleCorners(Math.min(0.3, Number(o.widthM) / 8), Math.min(0.3, Number(o.depthM) / 8), 0, o.eastM, o.northM), () => z0, () => z0 + trunk));
}

export function combineTriangles(...arrays) {
  const present = arrays.filter((a) => a?.length);
  if (!present.length) return null;
  if (present.length === 1) return present[0];
  const out = new Float32Array(present.reduce((n, a) => n + a.length, 0));
  let offset = 0;
  present.forEach((a) => { out.set(a, offset); offset += a.length; });
  return out;
}

/**
 * The roof surfaces of one scene, as { ring, top(p) } in a frame shifted by
 * `shift` (x, y, z): the PV face and the faces without PV.
 */
export function roofSurfaces(value, groundZ = () => 0, shift = { x: 0, y: 0, z: 0 }) {
  if (!value || siteGeometryErrors(value).length) return [];
  const sx = Number(shift.x) || 0, sy = Number(shift.y) || 0, sz = Number(shift.z) || 0;
  const move = (ring) => ring.map((p) => ({ x: p.x + sx, y: p.y + sy }));
  const out = [];
  if (value.roof) out.push({ ring: move(roofFootprint(value.roof)), top: (p) => roofZ(value.roof, p.x - sx, p.y - sy) + sz });
  (value.obstacles || []).filter((o) => o.kind === "roof").forEach((o) => out.push({
    ring: move(obstacleFootprint(o)), top: (p) => obstacleTopZ(value, o, { x: p.x - sx, y: p.y - sy }, groundZ) + sz,
  }));
  return out;
}

const onRing = (p, ring, tol) => ring.some((a, i) => segmentDistance(p, a, ring[(i + 1) % ring.length]) <= tol);

/**
 * Find the neighboring roof surface along a complete shared edge. The prism
 * draws a riser only for the upper surface, down to the lower roof surface.
 */
function adjacentRoofFace(self, faces) {
  return (a, b) => faces.find((face) => face !== self &&
    onRing(a, face.ring, 0.05) && onRing(b, face.ring, 0.05) &&
    onRing({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, face.ring, 0.05));
}

/** Metres east/north of the array; z=0 is the roof centre, or ground datum. */
export function buildSiteGeometry(value, groundZ = () => 0, { otherFaces = [] } = {}) {
  if (!value || siteGeometryErrors(value).length) return { roofMesh: null, obstacles: null, boundary: null };
  const roof = value.roof;
  const boundary = roof ? roofFootprint(roof) : null;
  const own = roofSurfaces(value, groundZ);
  const faces = [...own, ...(otherFaces || [])];
  const roofTop = (p) => roofZ(roof, p.x, p.y);
  const roofMesh = roof ? prism(boundary, () => -Number(roof.heightM), roofTop, adjacentRoofFace(own[0], faces)) : null;
  let faceIndex = roof ? 1 : 0;
  const obstacleMeshes = (value.obstacles || []).map((o) => {
    const corners = obstacleFootprint(o);
    const base = (p) => obstacleBaseZ(value, o, p, groundZ);
    const top = (p) => obstacleTopZ(value, o, p, groundZ);
    const adjacent = o.kind === "roof" ? adjacentRoofFace(own[faceIndex++], faces) : null;
    return o.kind === "tree" ? treeMesh(o, base) : prism(corners, base, top, adjacent);
  });
  const obstacles = combineTriangles(...obstacleMeshes);
  const solidObstacles = combineTriangles(...obstacleMeshes.filter((_, i) => value.obstacles[i].kind !== "tree"));
  return { roofMesh, obstacles, solidObstacles, boundary };
}

function overlaps(a, b) {
  return ![a, b].some((polygon) => polygon.some((p, i) => {
    const q = polygon[(i + 1) % polygon.length];
    const axis = { x: -(q.y - p.y), y: q.x - p.x };
    const project = (ring) => ring.map((v) => v.x * axis.x + v.y * axis.y);
    const av = project(a); const bv = project(b);
    return Math.max(...av) <= Math.min(...bv) + 1e-6 || Math.max(...bv) <= Math.min(...av) + 1e-6;
  }));
}

/** Check every real row, not just the smaller sample shown on screen. */
export function checkRoofRows(value, rows) {
  if (!value?.roof || siteGeometryErrors(value).length) return { outside: 0, intersecting: 0, belowRoof: 0 };
  const r = value.roof;
  const along = compass(Number(r.azimuthDeg) - 90);
  const across = compass(Number(r.azimuthDeg));
  let outside = 0; let intersecting = 0; let belowRoof = 0;
  const obstacles = (value.obstacles || []).map((o) => ({
    obstacle: o,
    ring: obstacleFootprint(o),
  }));
  rows.forEach((row) => {
    const corners = [row.low[0], row.low[1], row.high[1], row.high[0]];
    if (r.footprint ? !fitsFootprint(corners, r.footprint, Number(r.setbackM)) : corners.some((p) => Math.abs(dot(v3(p.x, p.y, 0), along)) > Number(r.widthM) / 2 - Number(r.setbackM) + 1e-6 ||
      Math.abs(dot(v3(p.x, p.y, 0), across)) > Number(r.depthM) / 2 - Number(r.setbackM) + 1e-6)) outside += 1;
    if (obstacles.some(({ obstacle: o, ring }) => {
      if (!triangulate(ring).some((ids) => overlaps(corners, ids.map((i) => ring[i])))) return false;
      const base = ring.map((p) => obstacleBaseZ(value, o, p));
      const top = ring.map((p) => obstacleTopZ(value, o, p));
      // An overlapping footprint below the roof is not a panel collision.
      return Math.max(...corners.map((p) => p.z)) > Math.min(...base) + 1e-6 &&
        Math.min(...corners.map((p) => p.z)) < Math.max(...top) - 1e-6;
    })) intersecting += 1;
    if (corners.some((p) => p.z < roofZ(r, p.x, p.y) - 1e-6)) belowRoof += 1;
  });
  return { outside, intersecting, belowRoof };
}
